import { type CoreEventBusEvents } from '@databases/core-event-bus';
import { container } from '@databases/service-container';
import { captureException } from '@sentry/core';

import { type Recyclable } from './types';

/**
 * Registers modules that recycle when bridge database identity changes
 * (contentId, Canvas reference, space). Calls setup(initialContentId) on all modules,
 * then subscribes to bridgeDatabaseIdentityChanged. On each event, disposes all modules
 * and calls setup(contentId from payload).
 *
 * @param modules - Recyclable modules to manage. Execution order is array order.
 * @param initialContentId - Content ID for the initial setup.
 * @returns A cleanup function that disposes all modules and unsubscribes from the event bus.
 *
 * Modules with `skipInitialSetup === true` skip the first `setup()` (registration only); identity-driven
 * `setupAll` calls still invoke `setup` for every module.
 */
export function registerRecyclableModules(
  modules: Recyclable[],
  initialContentId: string
): VoidFunction {
  let isFirstSetupPass = true;

  function setupAll(contentId: string) {
    for (const module of modules) {
      if (isFirstSetupPass && module.skipInitialSetup === true) {
        continue;
      }

      module.setup(contentId);
    }

    isFirstSetupPass = false;
  }

  function disposeAll() {
    for (const module of modules) {
      try {
        module.dispose();
      } catch (error) {
        captureException(error);
      }
    }
  }

  setupAll(initialContentId);

  const eventBus = container.resolve('EventBus');

  const onBridgeIdentityChanged = (
    data: CoreEventBusEvents['bridgeDatabaseIdentityChanged']
  ) => {
    disposeAll();
    setupAll(data.contentId);
  };

  eventBus.on('bridgeDatabaseIdentityChanged', onBridgeIdentityChanged);

  return () => {
    disposeAll();
    eventBus.remove('bridgeDatabaseIdentityChanged', onBridgeIdentityChanged);
  };
}
